import type { Game, GameData } from '@/types/games';
import type { Zone } from '@/types/zones/zone';
import type { CollectionSlice } from '@/types/zones/slices/collection';
import { getGameName, getProviderName } from '@/logic/game-adapter/game-adapter';

export const PLAYTECH_FALLBACK_GAME = {
    rgi: 'FGP',
    providerRef: 'ps',
    providerName: 'Playtech_poker',
    gameName: 'Multi table tournaments',
    gameUid: 'playtech-multi-table-tournaments'
} as const;

export type PlaytechGameConfig = {
    rgi: string;
    ganId?: string | number | null;
    providerRef: string;
    providerName: string;
    gameName: string;
    gameId?: string;
    gameUid: string;
};

const isPlaytechGame = (game: Game): boolean =>
    typeof game?.uid === 'string' && game.uid.includes('playtech') && !!game.data;

/**
 * Iterates all collection slices in the provided zones and returns the first
 * game whose uid contains "playtech". Returns null when nothing is found so the
 * caller can fall back to PLAYTECH_FALLBACK_GAME.
 */
export const findPlaytechGameInZones = (zones: Zone[]): Game | null => {
    for (const zone of zones) {
        for (const slice of zone.data.body) {
            const collectionSlice = slice as CollectionSlice;
            for (const game of collectionSlice.items ?? []) {
                if (isPlaytechGame(game)) {
                    return game;
                }
            }
        }
    }

    return null;
};

/**
 * Derives the launcher config for the first Playtech game found in the given
 * zones. Falls back to PLAYTECH_FALLBACK_GAME for every field that cannot be
 * resolved from the discovered game.
 */
export const getPlaytechGameConfig = (game: Game, isMobile: boolean): PlaytechGameConfig => {
    if (!game) {
        return { ...PLAYTECH_FALLBACK_GAME };
    }

    const { rgi, ganid, rgs_code_mobile, rgs_code_desktop, display_name, provider, provider_name } =
        game.data as GameData;

    const providerRef = isMobile ? rgs_code_mobile : rgs_code_desktop;
    const providerName = getProviderName(provider?.data?.providername) || provider_name;

    if (!rgi || !providerRef || !providerName) {
        console.error('[getPlaytechGameConfig] Playtech game is missing critical fields', {
            gameUid: game.uid,
            rgi,
            providerRef,
            providerName
        });
    }

    return {
        rgi,
        ganId: ganid,
        providerRef,
        providerName,
        gameName: getGameName(display_name),
        gameId: game.id,
        gameUid: game.uid
    };
};
